import React from "react";
import {
  CalculatorIcon,
  ChartLineIcon,
  DollarIcon,
  fmtCurrency,
  fmtNumber,
  fmtPercent,
  outcomeMeta,
  TruckIcon,
} from "./shared";
import { OverviewSection } from "./OverviewSection";
import { AnalysisSection } from "./AnalysisSection";
import { colors } from "../../../global";
import { AnalysisCard, FindingsSummary } from "./Cards";

export const GeneralInformation = ({ general_information }) => {
  const initial = general_information?.initial_offering ?? 0;
  const optimized = general_information?.optimised ?? 0;
  const meta = outcomeMeta(initial, optimized);

  const displayPct =
    typeof general_information?.percentage_reduction_incumbent_cora === "number"
      ? general_information.percentage_reduction_incumbent_cora
      : meta.pct;

  const absSavings = initial - optimized;

  const datasetCoverageDesc = `${fmtPercent(
    general_information?.percentage_cons_priced
  )} of consignments priced`;

  const optimizedLabel = meta.label;
  const optimizedBadge = meta.badge;
  const optimizedAccent = meta.accent;

  const isOpportunityPositive = absSavings > 0;
  const opportunityTitle = isOpportunityPositive
    ? "Potential Savings"
    : absSavings === 0
    ? "No Opportunity"
    : "Additional Cost";
  const opportunityDesc = isOpportunityPositive
    ? "Total opportunity identified"
    : absSavings === 0
    ? "No savings or loss"
    : "Extra cost vs incumbent";
  const opportunityAccent = isOpportunityPositive
    ? colors.success
    : absSavings === 0
    ? colors.info
    : colors.error;

  return (
    <>
      {/* Overview cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5 mb-8">
        <AnalysisCard
          title="Dataset Coverage"
          value={fmtNumber(general_information?.count_consignments)}
          description={datasetCoverageDesc}
          icon={CalculatorIcon}
          accentColor={colors.info}
          insight="Complete"
        />
        <AnalysisCard
          title="Current Cost"
          value={fmtCurrency(initial)}
          description="Incumbent pricing model"
          icon={DollarIcon}
          accentColor={colors.primary}
        />
        <AnalysisCard
          title={optimizedLabel}
          value={fmtCurrency(optimized)}
          description={meta.description}
          icon={ChartLineIcon}
          accentColor={optimizedAccent}
          insight={optimizedBadge}
        />
        <AnalysisCard
          title={opportunityTitle}
          value={fmtCurrency(Math.abs(absSavings))}
          description={opportunityDesc}
          icon={TruckIcon}
          accentColor={opportunityAccent}
          insight={
            isOpportunityPositive
              ? "Opportunity"
              : absSavings === 0
              ? "Neutral"
              : "Risk"
          }
        />
        <FindingsSummary meta={meta} displayPct={displayPct} />
      </div>
      {/* SECTION 2: Analysis & Comparison */}
      <div className="mx-auto">
        <AnalysisSection
          meta={meta}
          displayPct={displayPct}
          absSavings={absSavings}
        />

        {/* SECTION 3: Detailed Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
            <h4 className="text-md font-semibold text-gray-800 mb-4">
              Average Charge Weight Analysis
            </h4>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Current Average:</span>
                <span
                  className="font-semibold"
                  style={{ color: colors.primary }}
                >
                  {fmtNumber(general_information?.avg_inc_cw, 2)} kg
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Optimized Average:
                </span>
                <span
                  className="font-semibold"
                  style={{ color: colors.accent }}
                >
                  {fmtNumber(general_information?.avg_cora_cw, 2)} kg
                </span>
              </div>
              <div className="pt-2 border-t">
                <span className="text-xs text-gray-500">
                  {general_information?.avg_inc_cw ===
                  general_information?.avg_cora_cw
                    ? "No change in charge weight structure"
                    : "Charge weight structure changed"}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
            <h4 className="text-md font-semibold text-gray-800 mb-4">
              Cost per Consignment Analysis
            </h4>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Current Average:</span>
                <span
                  className="font-semibold"
                  style={{ color: colors.primary }}
                >
                  {fmtCurrency(general_information?.avg_cost_per_cons)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Optimized Average:
                </span>
                <span
                  className="font-semibold"
                  style={{ color: colors.accent }}
                >
                  {fmtCurrency(general_information?.avg_cost_per_cons_cora)}
                </span>
              </div>
              <div className="pt-2 border-t">
                <span
                  className="text-xs"
                  style={{
                    color:
                      meta.state === "worse" ? colors.error : colors.success,
                  }}
                >
                  {fmtCurrency(
                    Math.abs(
                      (general_information?.avg_cost_per_cons ?? 0) -
                        (general_information?.avg_cost_per_cons_cora ?? 0)
                    )
                  )}{" "}
                  {meta.state === "worse"
                    ? "extra per consignment"
                    : "savings per consignment"}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
            <h4 className="text-md font-semibold text-gray-800 mb-4">
              Surcharge Analysis
            </h4>
            <div className="space-y-4">
              <div className="text-center">
                <div
                  className="text-3xl font-bold"
                  style={{ color: colors.warning }}
                >
                  {typeof general_information?.surcharge_to_freight_ratio ===
                  "number"
                    ? `${general_information.surcharge_to_freight_ratio.toFixed(
                        1
                      )}%`
                    : "—"}
                </div>
                <div className="text-sm text-gray-600">
                  Surcharge to Freight Ratio
                </div>
              </div>
              <div className="pt-2 border-t space-y-2">
                <div className="flex justify-between text-xs">
                  <span>Total Surcharges:</span>
                  <span>
                    {fmtCurrency(
                      general_information?.total_incumbent_surcharges
                    )}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span>Freight Charge:</span>
                  <span>
                    {fmtCurrency(
                      general_information?.total_incumbent_freight_charge
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GeneralInformation;
